import { t } from 'i18next';
import { Button } from 'ncoded-component-library';
import MymeTypeIcon from '../../../../MymeTypeIcon';
import { useNavigate, useParams } from 'react-router-dom';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import ChooseAFolderInDr from 'router/subrouters/DataRoom/components/ChooseAFolderInDr';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { getDataRoomRoute, getIsNodeFolder, getNodeMimeType } from 'utils';

import './MoveFilesToFolder.styles.scss';
import './MoveFilesToFolder.styles.responsive.scss';

export type MoveFilesToFolderProps = {
  isOpen?: boolean;
  nodes: DataRoomNode[];
  nodeToMoveTo?: string;
  setNodeToMoveTo: (node: string) => void;
  closeMoveFiles?: () => void;
};

const MoveFilesToFolder: FC<MoveFilesToFolderProps> = ({
  nodes = [],
  isOpen,
  nodeToMoveTo,
  setNodeToMoveTo,
  closeMoveFiles,
}) => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const [isLoading, setIsLoading] = useState(false);
  const [folderName, setFolderName] = useState('');

  const selectedNumOfNodes = nodes.length;
  const isSingularNode = selectedNumOfNodes === 1 && nodes[0];
  const disabledSubmit = nodeToMoveTo === documentId || isLoading;

  const excludeFolderIds = useMemo(
    () =>
      nodes
        .filter(getIsNodeFolder)
        .map(({ _id }) => _id)
        .join(','),
    [nodes],
  );

  const onSubmit = useCallback(async () => {
    if (nodeToMoveTo === documentId) return;

    const filesIds = nodes.map(({ _id }) => _id);

    try {
      setIsLoading(true);
      await dataRoomApi.nodes.moveFilesToFolder(filesIds, nodeToMoveTo);

      setIsLoading(false);
      closeMoveFiles?.();
      popSuccess(
        t('successfullyMovedFilesToThisFolder', {
          count: selectedNumOfNodes,
          folderName,
        }),
      );
      navigate(
        `${getDataRoomRoute(dataRoomId, 'documents')}/${nodeToMoveTo || ''}`,
      );
    } catch (e) {
      setIsLoading(false);
      popServerError(e);
    }
  }, [
    dataRoomId,
    documentId,
    folderName,
    selectedNumOfNodes,
    nodes,
    nodeToMoveTo,
    closeMoveFiles,
    popServerError,
    popSuccess,
    navigate,
  ]);

  return (
    <>
      <div className="move-files-to-folder__title">
        <p className="drawer-title">
          {t('moveNumberItems', { count: selectedNumOfNodes })}
        </p>

        {isSingularNode && (
          <p className="move-files-to-folder__title__single-file">
            <MymeTypeIcon mimeType={getNodeMimeType(nodes[0])} />
            {nodes[0].name}
          </p>
        )}

        <p>{t('chooseNewDestination', { count: selectedNumOfNodes })}</p>
      </div>

      <div className="move-files-to-folder__folders">
        <ChooseAFolderInDr
          hasCreateFolder
          disabledFetch={!isOpen}
          dataRoomId={dataRoomId}
          currenntlySelectedNodeId={nodeToMoveTo}
          excludeFolderIds={excludeFolderIds}
          onChange={(nodeId, folderName) => {
            setNodeToMoveTo(nodeId);
            setFolderName(folderName);
          }}
        />
      </div>

      <div className="move-files-to-folder__actions">
        <Button variant="link" onClick={closeMoveFiles}>
          {t('cancel')}
        </Button>
        <Button type="submit" disabled={disabledSubmit} onClick={onSubmit}>
          {t('moveItems', { count: selectedNumOfNodes })}
        </Button>
      </div>
    </>
  );
};

export default MoveFilesToFolder;
